import * as React from "react";
import { ApplyModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/ApplyModel";
import { CheckboxGroup } from "../../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { EWizardLinterStatus, WizardLinter } from "../../../../../../Shared/Process/Process/WizardLinter";
import { ProductType } from "../../../../../../../Wrap/Constants/Enums/Domain/ProductType";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { GiaContributionModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/PersonalPortfolio/GiaContributionModel";
import { CashProductDeclarations } from "../../Review/Component/CashProductDeclarations";
import { InstrumentSelection } from "../../../../../../../Wrap/Models/Models/InstrumentSelection/InstrumentSelection";

interface IDeclarationsProps extends IProcessComponentProps {
    data: ApplyModel;
}

interface IDeclarationsState {
    hasChecked: boolean;
    isCashDeclarationChecked: boolean;
}

export class Declarations extends ProcessComponent<IDeclarationsProps, IDeclarationsState> {
    constructor(props: IDeclarationsProps) {
        super(props);
        this.state = {
            hasChecked: props.isReview,
            isCashDeclarationChecked: props.isReview,
        };
    }

    handleCashDeclarationChange = (event) => {
        this.setState({ isCashDeclarationChecked: event });
    };

    private getCashProductInvestments(): Array<InstrumentSelection> {
        const applyModelCashProducts: Array<InstrumentSelection> =
            (this.props.data as ApplyModel)?.GiaModel?.InstrumentSelections?.Instruments?.filter(
                (x) => x.Instrument.Type === ProductType.CashProduct
            ) ?? [];

        const giaContributionModelCashProducts: Array<InstrumentSelection> =
            (this.props.data as GiaContributionModel)?.Contribution?.InstrumentSelections?.Instruments?.filter(
                (x) => x.Instrument.Type === ProductType.CashProduct
            ) ?? [];

        return applyModelCashProducts.concat(giaContributionModelCashProducts);
    }

    private hasCashProducts(): boolean {
        var cashProducts = this.getCashProductInvestments();
        return cashProducts.length > 0;
    }

    isValid() {
        if (!this.state.hasChecked || (this.hasCashProducts() && !this.state.isCashDeclarationChecked)) {
            this.props.showErrorMsgPopUp(["You must accept all items to continue"], true);
        }
        return super.isValid() && this.state.hasChecked;
    }

    private renderDeclarations() {
        return (
            <>
                <WizardLinter status={EWizardLinterStatus.PleaseReview} enabled={true}>
                    <h2 className="mb-22">Declarations</h2>
                </WizardLinter>
                <p className="checked-paragraph">I confirm that</p>
                <ol className="ml-15">
                    <li className="mb-15">
                        I have been appointed by my client as their agent in respect of their account on the abrdn platform and understand that I will be responsible for any losses my client or abrdn incur if I act outside my authority as their agent.
                    </li>
                    <li className="mb-15">
                        I submit this application or instruction in accordance with the instructions and the full
                        authority of my client.
                    </li>
                    <li className="mb-15">
                        the address I entered on the platform is my client's personal home address.
                    </li>
                    <li className="mb-15">
                        my client is not a U.S. Person (as defined in the terms and conditions) and that I will inform abrdn immediately if my client becomes a U.S. Person.
                    </li>
                    <li className="mb-15">
                        I have provided my client with all pre-sale information required under applicable laws and
                        regulations, including (where applicable) the relevant
                        <ul>
                            <li className="mb-15">terms and conditions and/or policy provisions;</li>
                            <li className="mb-15">KFDs and KFIs;</li>
                            <li className="mb-15">KIDs, KIIDs and charges information documents (as applicable); and</li>
                            <li className="mb-15">SDR and investment label information documents (as applicable)</li>
                        </ul>
                        <div>
                            and that my client has had a reasonable opportunity to read these.
                        </div>
                    </li>
                    <li>
                        my client has signed the acceptance form of the relevant terms and conditions. I agree to obtain and retain the signed form and to submit it to abrdn immediately on request.  If I do not provide the acceptance form to abrdn prior to the account being opened, I will explain to my client that they will not receive paper copies of contract notes.
                    </li>
                    <li>
                        I will ensure that my client will meet the eligibility criteria of any investment held in their account as described in the relevant investment documentation.
                    </li>
                    <li>
                        I acknowledge that the account allocated to any application will not be activated until the relevant application has been accepted by abrdn.
                    </li>
                    <li>
                        all information submitted by me is accurate and complete and I agree to include the account
                        number allocated to my client in any documentation I submit.
                    </li>
                    <li>
                        any instruction to pay adviser charges, including the frequency and the amount, has been agreed
                        with my client.
                    </li>
                    <li>I have advised my client of abrdn's privacy policy.</li>
                    <li>
                        my client, their beneficial owner(s) (where applicable), any person acting on my client's behalf or paying into my client's account (“Verified Persons”) have been advised that identity verification checks have been carried out to comply with the Money Laundering, Terrorist Financing and Transfer of Funds (Information on the Payer) Regulations 2017.
                    </li>
                    <li>
                        the information in relation to any Verified Persons which I have provided to abrdn was obtained by me.
                    </li>
                    <li>
                        the evidence I have obtained to verify the identity of the Verified Persons
                        <ul>
                            <li className="mb-15">
                                meets the standard evidence set out within the Guidance for the UK Financial Sector
                                issued by JMLSG; or
                            </li>
                            <li className="mb-15">
                                exceeds the standard evidence, in which case any further verification evidence taken are provided to abrdn as part of this application. 
                            </li>
                        </ul>
                    </li>

                    <li>
                        I will retain the above mentioned evidence and provide it to abrdn immediately upon request.
                    </li>
                    <li>
                        I have not used the source of funds as evidence to verify the identity of the Verified Persons.
                    </li>
                    <li>
                        Any follow-up action required regarding the tax residency self-certification of my client has
                        been completed (where applicable).
                    </li>
                </ol>
                <CheckboxGroup
                    type="block"
                    wrapCss="mb-0"
                    options={[
                        {
                            label: "Confirm all",
                            disabled: this.props.isReview,
                            value: this.state.hasChecked,
                            onChange: (v) => {
                                if (v) {
                                    this.setState({ hasChecked: true });
                                } else {
                                    this.setState({ hasChecked: false });
                                }
                            },
                        },
                    ]}
                />
                {this.hasCashProducts() && (
                    <CashProductDeclarations
                        instrumentCodes={this.getCashProductInvestments().map((x) => x.Instrument.ProductCode)}
                        isCashDeclarationChecked={this.state.isCashDeclarationChecked}
                        onCashDeclarationChanged={this.handleCashDeclarationChange}
                    />
                )}
            </>
        );
    }

    render() {
        return <>{this.renderDeclarations()}</>;
    }
}
